import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import validate from 'validate.js';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import _ from 'underscore';
import {
	Button,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	Grid,
	Divider,
	colors,
	TextField
} from '@mui/material';

import { DatePicker, SelectField } from 'components';

import { SetFormState, SaveElementTaskDetails } from '../../../../../actions/ActivityViewActions.js';

const schema = {
	field: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const useStyles = makeStyles((theme) => ({
	root: {
		marginTop: theme.spacing(1),
	},

	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const DetailsForm = (props) => {
	const { className, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();

	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.activityViewReducer.formState);
	const task = useSelector((state) => state.activityViewReducer.task);
	const user = useSelector((state) => state.session.user);
	const elementTask = useSelector((state) => state.activityViewReducer.elementTask);

	useEffect(() => {
		const errors = validate(formState.values, schema);

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
		});
	}, [formState.values]);

	const _handleChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleDateChange = (value, picker) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[picker]: value
			},
			touched: {
				...formState.touched,
				[picker]: true,
			},
		});
	};

	const _hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));

	const _save = () => {
		const values = { ...formState.values, userId: user.id };
		dispatch(SaveElementTaskDetails(values));
	};

	return (
		<Card {...rest} className={clsx(classes.root, className)}>
			<CardHeader title="Details" />
			<Divider />
			<CardContent>
				<Grid container spacing={4}>
					<Grid item md={6} xs={12}>
						<TextField
							error={_hasError('scopeOfWork')}
							fullWidth
							helperText={
								_hasError('scopeOfWork') ? formState.errors.scopeOfWork[0] : null
							}
							label="Scope Of Work"
							name="scopeOfWork"
							onChange={_handleChange}
							value={formState.values.scopeOfWork || ''}
							variant="outlined"
							disabled={task && !task.scopeOfWork}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<TextField
							error={_hasError('owner')}
							fullWidth
							helperText={
								_hasError('owner') ? formState.errors.owner[0] : null
							}
							label="Owner"
							name="owner"
							onChange={_handleChange}
							value={formState.values.owner || ''}
							variant="outlined"
							disabled={task && !task.owner}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<DatePicker
							disabled={task && !task.requestDate}
							style={{ width: '100%' }}
							label="Request Date"
							value={formState.values.requestDate}
							onChange={(value) => _handleDateChange(value, 'requestDate')}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<DatePicker
							disabled={task && !task.receiveDate}
							style={{ width: '100%' }}
							label="Receive Date"
							value={formState.values.receiveDate}
							onChange={(value) => _handleDateChange(value, 'receiveDate')}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<DatePicker
							disabled={task && !task.sendDate}
							style={{ width: '100%' }}
							label="Send Date"
							value={formState.values.sendDate}
							onChange={(value) => _handleDateChange(value, 'sendDate')}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<DatePicker disabled={task && !task.date} style={{ width: '100%' }} label="Date" value={formState.values.date} onChange={(value) => _handleDateChange(value, 'date')} />
					</Grid>
					<Grid item md={6} xs={12}>
						<TextField
							error={_hasError('id')}
							fullWidth
							helperText={
								_hasError('id') ? formState.errors.id[0] : null
							}
							label="ID"
							name="id"
							onChange={_handleChange}
							value={formState.values.id || ''}
							variant="outlined"
							disabled={task && !task.id}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<TextField
							error={_hasError('vendor')}
							fullWidth
							helperText={
								_hasError('vendor') ? formState.errors.vendor[0] : null
							}
							label="Vendor"
							name="vendor"
							onChange={_handleChange}
							value={formState.values.vendor || ''}
							variant="outlined"
							disabled={task && !task.vendor}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<TextField
							error={_hasError('executerName')}
							fullWidth
							helperText={
								_hasError('executerName') ? formState.errors.executerName[0] : null
							}
							label="Executer Name"
							name="executerName"
							onChange={_handleChange}
							value={formState.values.executerName || ''}
							variant="outlined"
							disabled={task && !task.executerName}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<TextField
							error={_hasError('executerContact')}
							fullWidth
							helperText={
								_hasError('executerContact') ? formState.errors.executerContact[0] : null
							}
							label="Executer Contact"
							name="executerContact"
							onChange={_handleChange}
							value={formState.values.executerContact || ''}
							variant="outlined"
							disabled={task && !task.executerContact}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<TextField
							error={_hasError('technology')}
							fullWidth
							helperText={
								_hasError('technology') ? formState.errors.technology[0] : null
							}
							label="Technology"
							name="technology"
							onChange={_handleChange}
							value={formState.values.technology || ''}
							variant="outlined"
							disabled={task && !task.technology}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<TextField
							error={_hasError('incident')}
							fullWidth
							helperText={
								_hasError('incident') ? formState.errors.incident[0] : null
							}
							label="Incident"
							name="incident"
							onChange={_handleChange}
							value={formState.values.incident || ''}
							variant="outlined"
							disabled={task && !task.incident}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<TextField
							error={_hasError('notes')}
							fullWidth
							helperText={
								_hasError('notes') ? formState.errors.notes[0] : null
							}
							label="Notes"
							name="notes"
							onChange={_handleChange}
							value={formState.values.notes || ''}
							variant="outlined"
							disabled={task && !task.notes}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<SelectField
							id="status"
							name="statusId"
							value={formState.values.statusId || ''}
							onChange={_handleChange}
							hintText="Status"
							menuItems={(task && task.status) || []}
							error={_hasError('statusId') ? formState.errors.statusId[0] : null}
							disabled={task && !task.statusId}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<TextField
							error={_hasError('uf')}
							fullWidth
							helperText={
								_hasError('uf') ? formState.errors.uf[0] : null
							}
							label="UF"
							name="uf"
							onChange={_handleChange}
							value={formState.values.uf || ''}
							variant="outlined"
							disabled={task && !task.uf}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<TextField
							error={_hasError('regional')}
							fullWidth
							helperText={
								_hasError('regional') ? formState.errors.regional[0] : null
							}
							label="Regional"
							name="regional"
							onChange={_handleChange}
							value={formState.values.regional || ''}
							variant="outlined"
							disabled={task && !task.regional}
						/>
					</Grid>
					<Grid item md={12} xs={12}>
						<TextField
							error={_hasError('lessonsLearned')}
							fullWidth
							helperText={
								_hasError('lessonsLearned') ? formState.errors.lessonsLearned[0] : null
							}
							label="Lessons Learned"
							name="lessonsLearned"
							onChange={_handleChange}
							value={formState.values.lessonsLearned || ''}
							variant="outlined"
							multiline
							rows={3}
							rowMax={5}
							disabled={task && !task.lessonsLearned}
						/>
					</Grid>

				</Grid>
			</CardContent>
			<Divider />
			<CardActions>
				<Button
					className={classes.generate}
					variant="contained"
					onClick={_save}
					disabled={elementTask && _.isEqual(elementTask.details, formState.values)}
				>
					Save
				</Button>
			</CardActions>
		</Card>
	);
};

DetailsForm.propTypes = {
	className: PropTypes.object,
};

export default DetailsForm;
